import { EnumerationsLogic, CalculationResult, CalculationRowType, Dates, PensionCalculation, ResultRow, SummaryReportData, Arrays, Translations, Numeric, CalculationRowCategory, Language } from "@salaxy/core";
import { HeaderFooterData, ReportOptions, ReportLayout } from "../../model";

/**
 * Mapper for summary report
 */
export class SummaryMapper {
  private static calculationTypeMetaData = EnumerationsLogic.getEnumMetadata("CalculationRowType");
  /**
   * Creates HeaderFooterData from provided parameters
   * @param businessObject SummaryReportData that
   * @param options Report options
   * @param lang gets translation with this lang, if null use current lang
   * @returns HeaderFooterData for provided businessObject
   */
   public static getSummaryReportHeaderFooter(businessObject: SummaryReportData, options: ReportOptions, lang: Language = null): HeaderFooterData {
    const headerFooter: HeaderFooterData = {
      logo: options?.logo,
      img2: options?.img2,
      title: Translations.get("SALAXY.REPORTS.summary.header.title", null, lang),
      subTitle: `${Translations.get("SALAXY.REPORTS.summary.header.subtitle", null, lang)} ${Dates.getFormattedDate(businessObject.startDate)} - ${Dates.getFormattedDate(businessObject.endDate)}`,
      today: Dates.getFormattedDate(Dates.getToday()),
      date: Dates.getFormattedDate(Dates.getToday())
    };

    return headerFooter;
  }

  /**
   * Creates ReporLayout object depending on switchToPortrait boolean
   * @param switchToPortrait boolean indicating if layout should be portrait
   * @returns ReportLayout object
   */
   public static getReportLayout(switchToPortrait: boolean, pdf = false): ReportLayout {
    if (switchToPortrait) {
      return {
        isLayoutA4Portrait: true,
        isLayoutA4Landscape: false,
        layout: "a4Portrait",
        date: Dates.getFormattedDate(Dates.getToday()),
        isPreview: false,
        mode: pdf ? "pdf" : "htmlPage",
        isModeHtmlPage: !pdf,
        isModePdf: pdf,
      };
    } else {
      return {
        isLayoutA4Portrait: false,
        isLayoutA4Landscape: true,
        layout: "a4Landscape",
        date: Dates.getFormattedDate(Dates.getToday()),
        isPreview: false,
        mode: pdf ? "pdf" : "htmlPage",
        isModeHtmlPage: !pdf,
        isModePdf: pdf,
      };
    }
  }

  /**
   * Maps data to hbs supported report format
   * @param data Business Object for mapping
   * @returns Mapped data
   */
     public static getSummaryReport(data: SummaryReportData): SummaryReportData {
      (data as any).result.workerResult = [];
      (data as any).result.entrepreneurResult = [];
      (data as any).totalResult = [];
      for (const calc of data.calculations.filter(x => !this.isEntrepreneurPensionContract(x.result.responsibilities.pensionCalculation))) {
        const row: any = {};
        row.worker = calc.worker.avatar.displayName;
        row.workStartDate = Dates.getFormattedDate(calc.info.workStartDate);
        row.workStartEndDate = Dates.getFormattedDate(calc.info.workEndDate);
        row.totalGrossSalary = Numeric.formatNumber(calc.result.totals.totalGrossSalary);
        row.totalRowCategory = Numeric.formatNumber(this.getTotalByRowCategory(calc.result, CalculationRowCategory.Holidays));
        row.benefits = Numeric.formatNumber(calc.result.workerCalc.benefits);
        row.irCode = Numeric.formatNumber(this.getTotalByIrCode(calc.result, [401, 407, 419]));
        row.totalTaxable = Numeric.formatNumber(calc.result.totals.totalTaxable);
        row.tax = Numeric.formatNumber(calc.result.workerCalc.tax);
        row.pension = Numeric.formatNumber(calc.result.workerCalc.pension);
        row.unemploymentInsurance = Numeric.formatNumber(calc.result.workerCalc.unemploymentInsurance);
        row.unionPayment = Numeric.formatNumber(calc.result.workerCalc.unionPayment);
        row.foreclosure = Numeric.formatNumber(calc.result.workerCalc.foreclosure);
        row.otherDeductions = Numeric.formatNumber(calc.result.workerCalc.salaryAdvance + calc.result.workerCalc.otherDeductions + calc.result.workerCalc.prepaidExpenses);
        row.totalExpenses = Numeric.formatNumber(calc.result.totals.totalExpenses);
        row.totalWorkerPayment = Numeric.formatNumber(calc.result.workerCalc.totalWorkerPayment);
        row.totalPayment = Numeric.formatNumber(calc.result.employerCalc.totalPayment);
        row.palkkaus = Numeric.formatNumber(calc.result.employerCalc.palkkaus);
        row.service = Numeric.formatNumber(calc.result.employerCalc.service);
        row.socialSecurity = Numeric.formatNumber(calc.result.employerCalc.socialSecurity);
        row.totalPensions = Numeric.formatNumber(calc.result.totals.pension);
        row.employerPension = Numeric.formatNumber(calc.result.employerCalc.pension);
        row.unemployment = Numeric.formatNumber(calc.result.totals.unemployment);
        row.employerUnemployment = Numeric.formatNumber(calc.result.employerCalc.unemployment);
        row.taxAtSource = Numeric.formatNumber((calc.result.workerCalc as any).taxAtSource);
        row.workerSocialSecurity = Numeric.formatNumber((calc.result.workerCalc as any).socialSecurity);
        (data as any).result.workerResult.push(row);
      }

      for (const calc of data.calculations.filter(x => this.isEntrepreneurPensionContract(x.result.responsibilities.pensionCalculation))) {
        const row: any = {};
        row.worker = calc.worker.avatar.displayName;
        row.workStartDate = Dates.getFormattedDate(calc.info.workStartDate);
        row.workStartEndDate = Dates.getFormattedDate(calc.info.workEndDate);
        row.totalGrossSalary = Numeric.formatNumber(calc.result.totals.totalGrossSalary);
        row.totalRowCategory = Numeric.formatNumber(this.getTotalByRowCategory(calc.result, CalculationRowCategory.Holidays));
        row.benefits = Numeric.formatNumber(calc.result.workerCalc.benefits);
        row.irCode = Numeric.formatNumber(this.getTotalByIrCode(calc.result, [401, 407, 419]));
        row.totalTaxable = Numeric.formatNumber(calc.result.totals.totalTaxable);
        row.tax = Numeric.formatNumber(calc.result.workerCalc.tax);
        row.pension = Numeric.formatNumber(calc.result.workerCalc.pension);
        row.unemploymentInsurance = Numeric.formatNumber(calc.result.workerCalc.unemploymentInsurance);
        row.unionPayment = Numeric.formatNumber(calc.result.workerCalc.unionPayment);
        row.foreclosure = Numeric.formatNumber(calc.result.workerCalc.foreclosure);
        row.otherDeductions = Numeric.formatNumber(calc.result.workerCalc.salaryAdvance + calc.result.workerCalc.otherDeductions + calc.result.workerCalc.prepaidExpenses);
        row.totalExpenses = Numeric.formatNumber(calc.result.totals.totalExpenses);
        row.totalWorkerPayment = Numeric.formatNumber(calc.result.workerCalc.totalWorkerPayment);
        row.totalPayment = Numeric.formatNumber(calc.result.employerCalc.totalPayment);
        row.palkkaus = Numeric.formatNumber(calc.result.employerCalc.palkkaus);
        row.service = Numeric.formatNumber(calc.result.employerCalc.service);
        row.socialSecurity = Numeric.formatNumber(calc.result.employerCalc.socialSecurity);
        row.totalPensions = Numeric.formatNumber(calc.result.totals.pension);
        row.employerPension = Numeric.formatNumber(calc.result.employerCalc.pension);
        row.unemployment = Numeric.formatNumber(calc.result.totals.unemployment);
        row.employerUnemployment = Numeric.formatNumber(calc.result.employerCalc.unemployment);
        row.taxAtSource = Numeric.formatNumber((calc.result.workerCalc as any).taxAtSource);
        row.workerSocialSecurity = Numeric.formatNumber((calc.result.workerCalc as any).socialSecurity);
        (data as any).result.entrepreneurResult.push(row);
      }

      const totalRow: any = {};
      totalRow.totalGrossSalary = Numeric.formatNumber(data.result.totals.totalGrossSalary);
      totalRow.totalRowCategory = Numeric.formatNumber(this.getTotalByRowCategory(data.result, CalculationRowCategory.Holidays));
      totalRow.benefits = Numeric.formatNumber(data.result.workerCalc.benefits);
      totalRow.irCode = Numeric.formatNumber(this.getTotalByIrCode(data.result, [401, 407, 419]));
      totalRow.totalTaxable = Numeric.formatNumber(data.result.totals.totalTaxable);
      totalRow.tax = Numeric.formatNumber(data.result.workerCalc.tax);
      totalRow.pension = Numeric.formatNumber(data.result.workerCalc.pension);
      totalRow.unemploymentInsurance = Numeric.formatNumber(data.result.workerCalc.unemploymentInsurance);
      totalRow.unionPayment = Numeric.formatNumber(data.result.workerCalc.unionPayment);
      totalRow.foreclosure = Numeric.formatNumber(data.result.workerCalc.foreclosure);
      totalRow.otherDeductions = Numeric.formatNumber(data.result.workerCalc.salaryAdvance + data.result.workerCalc.otherDeductions + data.result.workerCalc.prepaidExpenses);
      totalRow.totalExpenses = Numeric.formatNumber(data.result.totals.totalExpenses);
      totalRow.totalWorkerPayment = Numeric.formatNumber(data.result.workerCalc.totalWorkerPayment);
      totalRow.totalPayment = Numeric.formatNumber(data.result.employerCalc.totalPayment);
      totalRow.palkkaus = Numeric.formatNumber(data.result.employerCalc.palkkaus);
      totalRow.service = Numeric.formatNumber(data.result.employerCalc.service);
      totalRow.socialSecurity = Numeric.formatNumber(data.result.employerCalc.socialSecurity);
      totalRow.totalPensions = Numeric.formatNumber(data.result.totals.pension);
      totalRow.employerPension = Numeric.formatNumber(data.result.employerCalc.pension);
      totalRow.unemployment = Numeric.formatNumber(data.result.totals.unemployment);
      totalRow.employerUnemployment = Numeric.formatNumber(data.result.employerCalc.unemployment);
      totalRow.taxAtSource = Numeric.formatNumber((data.result.workerCalc as any).taxAtSource);
      totalRow.workerSocialSecurity = Numeric.formatNumber((data.result.workerCalc as any).socialSecurity);
      (data as any).totalResult.push(totalRow);
      return data;
    }

    private static getTotalByRowCategory(calcResult: CalculationResult, category: CalculationRowCategory) {
      if (!calcResult) {
        return 0;
      }
      // This is done using the legacy way, change to category based method
      let rows: ResultRow[];

      switch(category) {
        case CalculationRowCategory.Holidays:
          rows = calcResult.rows.filter(x => this.isInRange(x.rowType, 300, 399));
          break;
        case CalculationRowCategory.Expenses:
          rows = calcResult.rows.filter(x => this.isInRange(x.rowType, 201, 299));
          break;
      }

      return Arrays.sum(rows, x => x.total);
    }

    private static getTotalByIrCode(calcResult: CalculationResult, irCodes: number[]) {
      if (!calcResult) {
        return 0;
      }
      return Arrays.sum(calcResult.irRows.filter(x => this.isInIrCodes(x.irData.code, irCodes)), x => x.total);
    }

    private static isEntrepreneurPensionContract(pensionCalculation: PensionCalculation) {
      return pensionCalculation == PensionCalculation.Entrepreneur ||
      pensionCalculation == PensionCalculation.Farmer ||
      pensionCalculation == PensionCalculation.Athlete ||
      pensionCalculation == PensionCalculation.SmallEntrepreneur ||
      pensionCalculation == PensionCalculation.SmallFarmer;
    }

    private static isInRange(rowType: CalculationRowType, min: number, max: number) {
      const enumNumberValue = this.calculationTypeMetaData.values.find(x => x.name == rowType)?.order;
      return enumNumberValue >= min && enumNumberValue <= max;
    }

    private static isInIrCodes(irCode: number, irCodes: number[] ) {
      return irCodes.indexOf(irCode) >= 0;
    }


}